(() => {
  if (customElements.get('drice-cart-recommendations')) return;
  const cache = new Map();
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'he', {
    style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
  }).format(cents / 100);
  async function recommendations(id, intent, root) {
    const key = `${root}:${id}:${intent}`;
    if (cache.has(key)) return cache.get(key);
    const promise = fetch(`${root}recommendations/products.json?product_id=${encodeURIComponent(id)}&limit=10&intent=${intent}`)
      .then(response => { if (!response.ok) throw new Error('Recommendations unavailable'); return response.json(); })
      .then(data => data.products || []);
    cache.set(key, promise);
    try { return await promise; } catch { cache.delete(key); return []; }
  }
  function announce(text) {
    let live = document.getElementById('dc-announcement');
    if (!live) {
      live = element('div', 'dc-sr-only');
      live.id = 'dc-announcement';
      live.setAttribute('role', 'status');
      live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%)';
      document.body.append(live);
    }
    live.textContent = text;
  }
  function refreshDrawer(html, cart) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const drawer = document.getElementById('t4s-mini_cart');
    for (const selector of ['[data-cart-items]', '[data-cart-prices]']) {
      const fresh = parsed.querySelector(selector);
      const old = drawer?.querySelector(selector);
      if (!fresh || !old) throw new Error('Cart view unavailable');
      old.innerHTML = fresh.innerHTML;
    }
    drawer.dataset.ccount = cart.item_count;
    document.querySelectorAll('[data-cart-count], [data-drice-cart-badge]').forEach(node => { node.textContent = cart.item_count; });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }
  async function changeQuantity(input, quantity) {
    const drawer = input.closest('#t4s-mini_cart');
    if (drawer.dataset.dcBusy === 'true') return;
    const previous = Number(input.dataset.currentQty);
    const maximum = Number(input.max);
    const message = document.createElement('p');
    message.className = 'dc-rec-error dc-cart-error';
    message.setAttribute('role', 'alert');
    drawer.querySelector('.dc-cart-error')?.remove();
    const showError = text => {
      message.textContent = text;
      const anchor = drawer.querySelector('.dc-checkout');
      if (anchor) anchor.before(message); else drawer.querySelector('[data-cart-items]')?.prepend(message);
      input.value = previous;
    };
    if (!Number.isInteger(quantity) || quantity < 0) { input.value = previous; return; }
    if (input.max && quantity > maximum) {
      showError(document.documentElement.lang.startsWith('he') ? 'זו הכמות הזמינה כרגע במידה שבחרת.' : 'This is the available quantity for your selected option.');
      return;
    }
    if (quantity === previous) return;
    drawer.dataset.dcBusy = 'true';
    drawer.setAttribute('aria-busy', 'true');
    const controls = [...drawer.querySelectorAll('button:not([data-drawer-close]), input, select')].filter(node => !node.disabled);
    controls.forEach(node => { node.disabled = true; });
    const id = input.dataset.id;
    let serverUpdated = false;
    try {
      const response = await fetch(`${rootPath()}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity, sections: ['mini_cart'], sections_url: location.pathname })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to update cart');
      serverUpdated = true;
      let html = cart.sections?.mini_cart;
      if (!html) {
        const result = await fetch(`${rootPath()}?section_id=mini_cart`);
        if (!result.ok) throw new Error('Unable to refresh cart');
        html = await result.text();
      }
      refreshDrawer(html, cart);
      announce(document.documentElement.lang.startsWith('he') ? 'הסל עודכן' : 'Cart updated');
    } catch (error) {
      if (serverUpdated) {
        // The cart changed on Shopify but the drawer could not be re-rendered: never leave the old quantity on screen.
        location.assign(`${rootPath()}cart`);
        return;
      }
      showError(document.documentElement.lang.startsWith('he') ? 'לא הצלחנו לעדכן את הכמות. רעננו את הסל ונסו שוב.' : error.message);
    } finally {
      drawer.dataset.dcBusy = 'false';
      drawer.removeAttribute('aria-busy');
      controls.forEach(node => { if (node.isConnected) node.disabled = false; });
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('#t4s-mini_cart [data-quantity-selector], #t4s-mini_cart [data-cart-remove]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const input = button.closest('[data-cart-item]')?.querySelector('[data-quantity-value]');
    if (!input) return;
    const increment = Number(input.step) || 1;
    const quantity = button.hasAttribute('data-cart-remove') ? 0 : Math.max(0, Number(input.dataset.currentQty) + (button.hasAttribute('data-increase-qty') ? increment : -increment));
    changeQuantity(input, quantity);
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches('#t4s-mini_cart [data-quantity-value]')) return;
    event.stopImmediatePropagation();
    changeQuantity(event.target, Number(event.target.value));
  }, true);
  class DriceRecommendations extends HTMLElement {
    connectedCallback() {
      this.index = 0;
      this.load();
    }
    async load() {
      const ids = [...new Set(this.dataset.productIds.split(',').filter(Boolean))];
      const existing = new Set(ids.map(String));
      const valid = product => product.available && !existing.has(String(product.id)) && !product.title.includes('מארז דרייס פרימיום') && product.variants?.some(variant => variant.available);
      const groups = await Promise.all(ids.slice(0, 4).map(async id => {
        const complementary = (await recommendations(id, 'complementary', this.dataset.root)).filter(valid);
        if (complementary.length) return complementary;
        return (await recommendations(id, 'related', this.dataset.root)).filter(valid);
      }));
      if (!this.isConnected) return;
      const unique = new Map();
      for (let rank = 0; rank < 10; rank++) {
        for (const products of groups) {
          const product = products[rank];
          if (product && !unique.has(product.id)) unique.set(product.id, product);
        }
      }
      this.products = [...unique.values()].slice(0, 6);
      if (!this.products.length) return;
      this.hidden = false;
      this.render();
    }
    render(focusDirection) {
      this.replaceChildren();
      const heading = element('div', 'dc-rec-heading');
      const titles = element('div');
      titles.append(element('h3', '', this.dataset.heading), element('p', '', this.dataset.subtitle));
      heading.append(titles);
      if (this.products.length > 1) {
        const nav = element('div', 'dc-rec-nav');
        for (const [direction, label, symbol] of [[-1, this.dataset.previous, '‹'], [1, this.dataset.next, '›']]) {
          const button = element('button', '', symbol);
          button.type = 'button';
          button.setAttribute('aria-label', label);
          button.dataset.direction = direction;
          button.addEventListener('click', () => {
            this.index = (this.index + direction + this.products.length) % this.products.length;
            this.render(direction);
          });
          nav.append(button);
        }
        heading.append(nav);
      }
      this.append(heading);
      const product = this.products[this.index];
      const card = element('div', 'dc-rec-card');
      const image = element('img', 'dc-rec-image');
      const source = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url;
      if (source) {
        const url = new URL(source, location.origin);
        url.searchParams.set('width', '240');
        image.src = url.href;
      }
      image.alt = product.title;
      image.width = 90;
      image.height = 110;
      image.loading = 'lazy';
      card.append(image);
      const details = element('div');
      const title = element('a', 'dc-rec-title', product.title);
      title.href = product.url || `${this.dataset.root}products/${encodeURIComponent(product.handle)}`;
      const variants = product.variants.filter(variant => variant.available);
      let selected = variants[0];
      const price = element('div', 'dc-rec-price', money(selected.price, this.dataset.currency));
      const actions = element('div', 'dc-rec-actions');
      if (variants.length > 1 || selected.title !== 'Default Title') {
        const select = element('select');
        select.setAttribute('aria-label', `${this.dataset.options}: ${product.title}`);
        for (const variant of variants) {
          const option = element('option', '', variant.title);
          option.value = variant.id;
          select.append(option);
        }
        select.addEventListener('change', () => {
          selected = variants.find(variant => String(variant.id) === select.value);
          price.textContent = money(selected.price, this.dataset.currency);
        });
        actions.append(select);
      }
      const add = element('button', 'dc-rec-add', this.dataset.add);
      add.type = 'button';
      add.setAttribute('aria-label', `${this.dataset.add}: ${product.title}`);
      add.addEventListener('click', () => this.add(selected.id, add));
      actions.append(add);
      details.append(title, price, actions);
      card.append(details);
      this.append(card);
      this.error = element('p', 'dc-rec-error');
      this.error.setAttribute('role', 'alert');
      this.append(this.error);
      if (focusDirection) this.querySelector(`[data-direction="${focusDirection}"]`)?.focus({ preventScroll: true });
    }
    async add(id, button) {
      const drawer = this.closest('#t4s-mini_cart');
      if (!drawer || drawer.dataset.dcBusy === 'true' || drawer.classList.contains('is--contentUpdate')) return;
      drawer.dataset.dcBusy = 'true';
      const controls = [...drawer.querySelectorAll('button:not([data-drawer-close]), input, select')].filter(node => !node.disabled);
      controls.forEach(node => { node.disabled = true; });
      button.textContent = this.dataset.adding;
      this.error.textContent = '';
      let added = false;
      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: ['mini_cart'], sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.dataset.error);
        added = true;
        const cartResponse = await fetch(`${rootPath()}cart.js`, { cache: 'no-store' });
        if (!cartResponse.ok) throw new Error(this.dataset.error);
        const cart = await cartResponse.json();
        let html = result.sections?.mini_cart;
        if (!html) {
          const sectionResponse = await fetch(`${rootPath()}?section_id=mini_cart`);
          if (!sectionResponse.ok) throw new Error(this.dataset.error);
          html = await sectionResponse.text();
        }
        refreshDrawer(html, cart);
        announce(this.dataset.added);
        drawer.querySelector('[data-drawer-close]')?.focus({ preventScroll: true });
      } catch (error) {
        if (added) {
          location.assign(`${rootPath()}cart`);
          return;
        }
        this.error.textContent = error.message || this.dataset.error;
      } finally {
        drawer.dataset.dcBusy = 'false';
        controls.forEach(node => { if (node.isConnected) node.disabled = false; });
        if (button.isConnected) button.textContent = this.dataset.add;
      }
    }
  }
  customElements.define('drice-cart-recommendations', DriceRecommendations);
  document.addEventListener('keydown', () => document.documentElement.classList.add('dc-keyboard'), { passive: true });
  document.addEventListener('pointerdown', () => document.documentElement.classList.remove('dc-keyboard'), { passive: true });
  document.addEventListener('click', event => {
    if (event.target.closest('#t4s-mini_cart[data-dc-busy="true"] [data-cart-remove], #t4s-mini_cart[data-dc-busy="true"] [data-action-quickshop]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
})();
